import React, { useEffect, useState } from 'react';
import { consigneesAPI, warehouseRowsAPI } from '../../services/api';
import Table from '../Common/Table';
import Modal from '../Common/Modal';

const WarehouseRowsManagement = () => {
  const [rows, setRows] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({
    consignee_id: '',
    row_name: '',
    row_number: '',
    is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rowsData, consigneesData] = await Promise.all([
        warehouseRowsAPI.getAll(),
        consigneesAPI.getAll(),
      ]);
      setRows(rowsData);
      setConsignees(consigneesData);
    } catch (error) {
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      consignee_id: '',
      row_name: '',
      row_number: '',
      is_active: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        consignee_id: parseInt(formData.consignee_id),
        row_name: formData.row_name,
        row_number: formData.row_number === '' ? null : parseInt(formData.row_number),
        is_active: !!formData.is_active,
      };
      if (editingRow) {
        await warehouseRowsAPI.update(editingRow.id, submitData);
      } else {
        await warehouseRowsAPI.create(submitData);
      }
      setShowModal(false);
      setEditingRow(null);
      resetForm();
      loadData();
    } catch (error) {
      alert('Error saving warehouse row: ' + error.message);
    }
  };

  const handleEdit = (item) => {
    setEditingRow(item);
    setFormData({
      consignee_id: item.consignee_id,
      row_name: item.row_name || '',
      row_number: item.row_number ?? '',
      is_active: !!item.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this warehouse row? This may also delete related bins.')) return;
    try {
      await warehouseRowsAPI.delete(id);
      loadData();
    } catch (error) {
      alert('Error deleting warehouse row: ' + error.message);
    }
  };

  const columns = [
    { key: 'consignee_name', label: 'Consignee' },
    { key: 'row_name', label: 'Row Name' },
    { key: 'row_number', label: 'Row #' },
    { key: 'is_active', label: 'Active', format: (val) => (val ? 'Yes' : 'No') },
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Warehouse Rows</h1>
        <button className="btn-primary" onClick={() => { setEditingRow(null); resetForm(); setShowModal(true); }}>
          Add Warehouse Row
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table data={rows} columns={columns} onEdit={handleEdit} onDelete={handleDelete} />
      )}

      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingRow(null); resetForm(); }}
        title={editingRow ? 'Edit Warehouse Row' : 'Add Warehouse Row'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Consignee *</label>
            <select
              value={formData.consignee_id}
              onChange={(e) => setFormData({ ...formData, consignee_id: e.target.value })}
              required
            >
              <option value="">Select Consignee</option>
              {consignees.map((c) => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Row Name *</label>
              <input
                type="text"
                value={formData.row_name}
                onChange={(e) => setFormData({ ...formData, row_name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Row Number</label>
              <input
                type="number"
                value={formData.row_number}
                onChange={(e) => setFormData({ ...formData, row_number: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={!!formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />{' '}
              Active
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingRow(null); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default WarehouseRowsManagement;

